import './request-body-info.scss';

import { Modal } from 'antd';
import { useMemo, useState } from 'react';

import { Button, CircledImage } from '@/components/Shared/ui';
import { BankNames, DictTypes } from '@/constants';
import FileTextIcon from '@/media/icons/file-text.svg?react';
import { copyToClipboard } from '@/services/helpers/copyToClipboard';
import { safeKey } from '@/services/helpers/dict';
import { useAppSelector } from '@/store';

import type { TBankOffers } from '../../types/bank-offer.types';

export type TRequestBodyInfoProps = { bankName: BankNames } & Pick<
  TBankOffers,
  'responsePrimaryBody' | 'responseSendedBody'
>;

function getRequestBodyByBank({
  bankName,
  responsePrimaryBody,
  responseSendedBody
}: TRequestBodyInfoProps): string {
  switch (bankName) {
    case BankNames.bcc:
    case BankNames.halyk:
    case BankNames.sapa:
      return responseSendedBody || responsePrimaryBody || '';
    case BankNames.altyn:
      return responseSendedBody || '';
    case BankNames.bereke:
    case BankNames.forte:
    case BankNames.shinhan:
    case BankNames.solva:
      return responsePrimaryBody || '';
    default:
      return '';
  }
}

const RequestBodyInfo: React.FC<TRequestBodyInfoProps> = ({
  bankName,
  responsePrimaryBody,
  responseSendedBody
}) => {
  const {
    userAccesses: {
      global: { isAdmin }
    }
  } = useAppSelector((state) => state.userInfo);
  const { dicts } = useAppSelector((state) => state.dict);
  const [isOpened, setIsOpened] = useState(false);

  const bank = dicts?.[DictTypes.Banks]?.getBy.name[safeKey(bankName)];

  const formattedRequestBody: string | null = useMemo(() => {
    const requestBody = getRequestBodyByBank({
      bankName,
      responsePrimaryBody,
      responseSendedBody
    });

    if (!requestBody) return null;
    try {
      const parsedRequestBody = JSON.parse(requestBody);
      try {
        return JSON.stringify(JSON.parse(parsedRequestBody), null, 2);
      } catch {
        return JSON.stringify(parsedRequestBody, null, 2);
      }
    } catch {
      return requestBody;
    }
  }, [bankName, responsePrimaryBody, responseSendedBody]);

  const handleClose = () => {
    setIsOpened(false);
  };

  if (!isAdmin || !formattedRequestBody) return null;
  return (
    <>
      <Button size="small" onClick={() => setIsOpened(true)}>
        <FileTextIcon className="request-body-info__icon" /> Тело запроса
      </Button>
      <Modal
        centered
        mask={{ closable: true }}
        className="request-body-info"
        title={
          <div className="request-body-info__title-wrapper">
            <div className="request-body-info__title-bank">
              <CircledImage
                src={bank?.icon || bank?.name}
                alt={bankName}
                size={40}
              />
              {bank?.fullname}
            </div>
            <h3 className="request-body-info__title">Тело запроса</h3>
          </div>
        }
        open={isOpened}
        onCancel={handleClose}
        footer={
          <div className="request-body-info__footer">
            <Button
              type="button"
              onClick={() => copyToClipboard(formattedRequestBody)}
            >
              Скопировать
            </Button>
            <Button type="button" onClick={handleClose}>
              Закрыть
            </Button>
          </div>
        }
        width={800}
      >
        <pre className="request-body-info__pre">{formattedRequestBody}</pre>
      </Modal>
    </>
  );
};

export default RequestBodyInfo;
